import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

import { SkillDocumentResponse } from '../../../models/skill-learning.model';

export type DocumentUploaderMode = 'empty' | 'selected' | 'uploaded';
type SkillDocumentInput = SkillDocumentResponse | null;

const MAX_DOCUMENT_SIZE_BYTES = 20971520;
const DOCUMENT_SIZE_ERROR_KEY = 'SKILL_LEARNING_DOCUMENT_UPLOADER_SIZE_LIMIT';

@Component({
  selector: 'app-document-uploader',
  templateUrl: './document-uploader.component.html',
  styleUrls: ['./document-uploader.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentUploaderComponent {

  @Input() set fileId(value: string | null) {
    this._fileId = value;
    this.updateMode();
  }

  get fileId(): string | null {
    return this._fileId;
  }

  @Input() set fileName(value: string | null) {
    this._fileName = value;
    this.updateMode();
  }

  get fileName(): string | null {
    return this._fileName;
  }

  @Input() set document(value: SkillDocumentInput) {
    this._document = value;
    this.updateMode();
  }

  get document(): SkillDocumentInput {
    return this._document;
  }

  @Input() label = 'SKILL_LEARNING_DOCUMENT_UPLOADER_SELECT_DOCUMENT';
  @Input() hintKey = 'SKILL_LEARNING_DOCUMENT_UPLOADER_SUPPORTED_FORMATS';
  @Input() acceptTypes = '.pdf,.docx';
  @Input() isUploading = false;

  @Output() fileSelected = new EventEmitter<File | null>();
  @Output() previewRequested = new EventEmitter<void>();
  @Output() downloadRequested = new EventEmitter<void>();

  mode: DocumentUploaderMode = 'empty';
  localFile: File | null = null;
  validationErrorKey = '';

  private _fileId: string | null = null;
  private _fileName: string | null = null;
  private _document: SkillDocumentInput = null;

  constructor(private cdr: ChangeDetectorRef) {}

  get displayName(): string {
    if (this.localFile) {
      return this.localFile.name;
    }
    if (this._document && this._document.fileName) {
      return this._document.fileName;
    }
    return this._fileName;
  }

  get selectedFileSizeLabel(): string {
    if (!this.localFile) {
      return '';
    }
    return this.formatSize(this.localFile.size);
  }

  get isSelected(): boolean {
    return this.localFile !== null;
  }

  get isUploaded(): boolean {
    return this._fileId !== null;
  }

  get hasFile(): boolean {
    return this.displayName !== null;
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = this.resolveInputFile(input);
    this.clearValidationError();
    if (!file) {
      this.clearLocalSelection();
      return;
    }
    if (this.isFileTooLarge(file)) {
      this.rejectFile(input);
      return;
    }
    this.selectFile(file);
  }

  onCancelSelection(): void {
    this.clearSelection();
    this.fileSelected.emit(null);
  }

  onDownload(): void {
    this.downloadRequested.emit();
  }

  onPreview(): void {
    this.previewRequested.emit();
  }

  clearSelection(): void {
    this.clearValidationError();
    this.clearLocalSelection();
  }

  private clearLocalSelection(): void {
    this.localFile = null;
    this.updateMode();
  }

  private updateMode(): void {
    if (this.localFile) {
      this.mode = 'selected';
      return;
    }
    if (this._fileId) {
      this.mode = 'uploaded';
      return;
    }
    this.mode = 'empty';
    this.cdr.markForCheck();
  }

  private resolveInputFile(input: HTMLInputElement): File | null {
    if (!input.files || input.files.length === 0) {
      return null;
    }
    return input.files[0];
  }

  private isFileTooLarge(file: File): boolean {
    return file.size > MAX_DOCUMENT_SIZE_BYTES;
  }

  private rejectFile(input: HTMLInputElement): void {
    input.value = '';
    this.validationErrorKey = DOCUMENT_SIZE_ERROR_KEY;
    this.cdr.markForCheck();
  }

  private selectFile(file: File): void {
    this.localFile = file;
    this.mode = 'selected';
    this.fileSelected.emit(this.localFile);
    this.cdr.markForCheck();
  }

  private clearValidationError(): void {
    this.validationErrorKey = '';
  }

  private formatSize(size: number): string {
    if (size < 1024) {
      return size + ' B';
    }
    if (size < 1048576) {
      return (size / 1024).toFixed(1) + ' KB';
    }
    return (size / 1048576).toFixed(1) + ' MB';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/document-uploader/document-uploader.component.ts